import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import Pagination from "./shared";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import api from "../../lib/api";
import { toast } from "sonner";
import { Download, Search } from "lucide-react";

export default function AdminAudit() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ actor: "", action: "", entity_id: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), page_size: "50" });
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    try {
      const r = await api.get(`/admin/audit?${params.toString()}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, filters]);

  const exportCsv = () => {
    if (items.length === 0) { toast.error("Nothing to export"); return; }
    const header = ["timestamp", "actor_email", "action", "entity_type", "entity_id", "reason"];
    const rows = items.map((i) => header.map((h) => JSON.stringify(i[h] ?? "")).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success(`Exported ${items.length} rows`);
  };

  return (
    <AdminLayout
      title="Audit Log"
      subtitle="Append-only record of every staff action. Read-only."
      actions={<Button onClick={exportCsv} className="bg-primary hover:bg-primary/90 text-white rounded-xl gap-1.5" data-testid="audit-export-btn"><Download className="w-4 h-4" /> Export CSV</Button>}
    >
      <div className="xch-card p-4 mb-4">
        <div className="grid md:grid-cols-3 gap-3">
          <FilterInput label="Actor" value={filters.actor} onChange={(v) => { setFilters({ ...filters, actor: v }); setPage(1); }} testid="audit-filter-actor" />
          <FilterInput label="Action" value={filters.action} onChange={(v) => { setFilters({ ...filters, action: v }); setPage(1); }} testid="audit-filter-action" />
          <FilterInput label="Entity ID" value={filters.entity_id} onChange={(v) => { setFilters({ ...filters, entity_id: v }); setPage(1); }} testid="audit-filter-entity" mono />
        </div>
      </div>

      <div className="xch-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead>
              <tr className="bg-muted/50 border-b border-border">
                <Th>Timestamp</Th><Th>Actor</Th><Th>Action</Th><Th>Entity</Th><Th>Reason</Th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="text-center py-10 text-muted-foreground text-sm">Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-muted-foreground text-sm">No audit entries match these filters.</td></tr>}
              {items.map((a) => (
                <tr key={a.id} className="border-b border-border" data-testid={`audit-row-${a.id}`}>
                  <td className="px-4 py-2.5 text-xs font-mono text-muted-foreground tabular-nums whitespace-nowrap">{new Date(a.timestamp).toLocaleString("en-IE")}</td>
                  <td className="px-4 py-2.5 text-xs">{a.actor_email}</td>
                  <td className="px-4 py-2.5 text-xs"><span className="font-mono bg-muted px-1.5 py-0.5 rounded">{a.action}</span></td>
                  <td className="px-4 py-2.5 text-xs"><span className="uppercase font-semibold text-muted-foreground">{a.entity_type}</span> <span className="font-mono text-muted-foreground">{a.entity_id?.slice(0, 8)}…</span></td>
                  <td className="px-4 py-2.5 text-xs text-foreground">{a.reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 pb-3">
          <Pagination page={page} pageSize={50} total={total} onPage={setPage} />
        </div>
      </div>
    </AdminLayout>
  );
}

const Th = ({ children }) => (
  <th className="px-4 py-2.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground text-left">{children}</th>
);

const FilterInput = ({ label, value, onChange, testid, mono }) => (
  <div className="space-y-1.5">
    <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</div>
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
      <Input value={value} onChange={(e) => onChange(e.target.value)} className={`rounded-xl pl-9 ${mono ? "font-mono text-xs" : ""}`} data-testid={testid} />
    </div>
  </div>
);
